/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';
import { Input, QtyInput as BaseQtyInput } from '../../../../styles/ui/Form';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { DataTable } from '../../../../styles/ui/Table';
import { FlagRow, FooterNote, ModalIntro } from './modalKit';
import { DIVERGENCE_REASON_MIN } from '../constants';
import { productName, routeLabel } from '../utils/format';

// =============================================================================
// CONFERÊNCIA da remessa (POST /transfer-shipments/:id/receive).
//
// O estoque entra no destino PELO CONTADO — não pelo expedido. Regras do
// contrato espelhadas na UI antes do submit:
//   · a conferência cobre TODOS os itens da remessa (não há "conferir depois");
//   · contado ≤ expedido (sobra não entra — 422 transfer_receive_over_shipped);
//   · contado 0 é válido (perda integral);
//   · falta exige motivo por item (mín. 5 caracteres).
//
// Anatomia da refatoração visual (2026-09-10): o campo de motivo é CONDICIONAL
// e mora DENTRO da linha em falta (linha-filha âmbar), não numa coluna fixa —
// o campo só existe quando a condição se aplica. Linha em falta fica âmbar;
// contado acima do expedido marca a linha em vermelho (o servidor recusaria).
//
// Falta deixa a remessa RECEIVED_WITH_DIVERGENCE — o gestor registra depois a
// tratativa, que fecha a contagem sem mexer em estoque.
// =============================================================================

const InlineError = styled.small`
   display: block;
   margin-top: 6px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ theme }) => theme.colors.danger};
`;

const QtyInput = styled(BaseQtyInput)`
   max-width: 100px;
`;

const ReasonCell = styled.td`
   && {
      padding-top: 0;
      border-top: none;
   }

   .reason-label {
      display: block;
      margin-bottom: 4px;
      font-size: 11px;
      font-weight: 700;
      color: #8a5a00;
   }
`;

const integerRegex = /^[0-9]+$/;

export default function ReceiveModal({
   isOpen,
   shipment,
   isSubmitting = false,
   itemErrors = {},
   onCancel,
   onConfirm,
}) {
   const [counted, setCounted] = useState({});
   const [reasons, setReasons] = useState({});
   const [touched, setTouched] = useState(false);

   const items = shipment?.items ?? [];

   // Pré-preenche com o expedido: o caso comum é "chegou tudo", e o operador só
   // corrige o que faltou.
   useEffect(() => {
      if (!isOpen) return;
      setTouched(false);
      setReasons({});
      setCounted(
         Object.fromEntries(
            items.map((item) => [item.prod_id, String(item.shipped_qtd)])
         )
      );
      // `items` deriva de `shipment`; depender do objeto evita recriar o estado
      // a cada render do pai.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, shipment]);

   const rows = items.map((item) => {
      const raw = counted[item.prod_id];
      const hasValue = raw !== undefined && raw !== '';
      const receivedQtd = hasValue ? Number(raw) : null;
      // O 422 estruturado (handoff 0107 §5.3) traz o `shipped_qtd` ATUAL do
      // servidor — sob concorrência ele corrige a referência da linha.
      const shippedQtd = Number(
         itemErrors[item.prod_id]?.shipped_qtd ?? item.shipped_qtd
      );
      const isOver = receivedQtd !== null && receivedQtd > shippedQtd;
      const isShort = receivedQtd !== null && receivedQtd < shippedQtd;
      const reason = (reasons[item.prod_id] ?? '').trim();
      const needsReason = isShort && reason.length < DIVERGENCE_REASON_MIN;
      return {
         item,
         raw,
         hasValue,
         receivedQtd,
         shippedQtd,
         isOver,
         isShort,
         needsReason,
      };
   });

   const hasDivergence = rows.some((row) => row.isShort);
   const shortTotal = rows.reduce(
      (acc, row) =>
         row.isShort ? acc + (row.shippedQtd - row.receivedQtd) : acc,
      0
   );
   const hasOver = rows.some((row) => row.isOver);
   const isComplete = rows.every((row) => row.hasValue);
   const hasBlockingError = rows.some((row) => row.isOver || row.needsReason);
   const canConfirm = isComplete && !hasBlockingError && !isSubmitting;

   const handleConfirm = () => {
      setTouched(true);
      if (!canConfirm) return;
      onConfirm({
         items: rows.map((row) => ({
            prodId: row.item.prod_id,
            receivedQtd: row.receivedQtd,
            divergenceReason: row.isShort
               ? (reasons[row.item.prod_id] ?? '').trim()
               : '',
         })),
      });
   };

   // Nota do footer: o estado que importa agora, na ordem bloqueio > falta > ok.
   let footerNote = null;
   if (hasOver) {
      footerNote = (
         <FooterNote $warn>
            Contado acima do expedido — o servidor recusaria a conferência.
         </FooterNote>
      );
   } else if (hasDivergence) {
      footerNote = (
         <FooterNote $warn>
            Falta de {shortTotal} un — a remessa ficará com divergência.
         </FooterNote>
      );
   } else {
      footerNote = (
         <FooterNote>
            Contado = expedido em todos os itens. Corrija só o que faltou.
         </FooterNote>
      );
   }

   return (
      <Modal
         isOpen={isOpen}
         title="Conferir remessa"
         subtitle={
            shipment
               ? `Remessa #${shipment.id} · ${routeLabel(shipment)}`
               : null
         }
         onClose={isSubmitting ? undefined : onCancel}
         maxWidth="900px"
         footer={
            <>
               {footerNote}
               <GhostButton
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
               >
                  {isSubmitting ? 'Registrando…' : 'Registrar conferência'}
               </PrimaryButton>
            </>
         }
      >
         <ModalIntro $tone="info">
            Informe o que foi <strong>efetivamente contado</strong> em cada item
            — é essa quantidade que entra no estoque do destino. Todos os itens
            precisam ser conferidos; contado zero é válido (perda integral).
         </ModalIntro>

         <TableWrapper $minWidth="560px">
            <DataTable>
               <thead>
                  <tr>
                     <th className="center">SKU</th>
                     <th>Produto</th>
                     <th className="num">Expedido</th>
                     <th className="center">Contado</th>
                  </tr>
               </thead>
               <tbody>
                  {rows.map((row) => {
                     const flag =
                        (row.isOver && 'invalid') ||
                        (row.isShort && 'short') ||
                        null;
                     return (
                        <React.Fragment key={row.item.prod_id}>
                           <FlagRow $flag={flag}>
                              <td className="center">
                                 {row.item.prod_sku ?? '—'}
                              </td>
                              <td>{productName(row.item)}</td>
                              <td className="num">{row.shippedQtd}</td>
                              <td className="center">
                                 <QtyInput
                                    type="text"
                                    inputMode="numeric"
                                    aria-label={`Quantidade contada de ${
                                       row.item.prod_name ?? row.item.prod_id
                                    }`}
                                    aria-invalid={row.isOver || undefined}
                                    $invalid={row.isOver}
                                    value={row.raw ?? ''}
                                    disabled={isSubmitting}
                                    onChange={(event) => {
                                       const { value } = event.target;
                                       if (
                                          value !== '' &&
                                          !integerRegex.test(value)
                                       ) {
                                          return;
                                       }
                                       setCounted((prev) => ({
                                          ...prev,
                                          [row.item.prod_id]: value,
                                       }));
                                    }}
                                 />
                                 {row.isOver && (
                                    <InlineError>
                                       Acima do expedido.
                                    </InlineError>
                                 )}
                                 {touched && !row.hasValue && (
                                    <InlineError>Obrigatório.</InlineError>
                                 )}
                              </td>
                           </FlagRow>

                           {/* O motivo SÓ existe na linha em falta — entra como
                               linha-filha, na mesma tinta âmbar do item. */}
                           {row.isShort && (
                              <FlagRow $flag="short">
                                 <ReasonCell colSpan={4}>
                                    <span className="reason-label">
                                       Motivo da falta (
                                       {row.shippedQtd - row.receivedQtd} un) —
                                       obrigatório
                                    </span>
                                    <Input
                                       type="text"
                                       aria-label={`Motivo da divergência de ${
                                          row.item.prod_name ?? row.item.prod_id
                                       }`}
                                       aria-invalid={
                                          (touched && row.needsReason) ||
                                          undefined
                                       }
                                       $invalid={touched && row.needsReason}
                                       value={reasons[row.item.prod_id] ?? ''}
                                       disabled={isSubmitting}
                                       placeholder="Ex.: 1 un. quebrada no transporte"
                                       onChange={(event) =>
                                          setReasons((prev) => ({
                                             ...prev,
                                             [row.item.prod_id]:
                                                event.target.value,
                                          }))
                                       }
                                    />
                                    {touched && row.needsReason && (
                                       <InlineError>
                                          Descreva a falta com pelo menos{' '}
                                          {DIVERGENCE_REASON_MIN} caracteres.
                                       </InlineError>
                                    )}
                                 </ReasonCell>
                              </FlagRow>
                           )}
                        </React.Fragment>
                     );
                  })}
               </tbody>
            </DataTable>
         </TableWrapper>
      </Modal>
   );
}

ReceiveModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   shipment: PropTypes.object,
   isSubmitting: PropTypes.bool,
   // { [prod_id]: violação estruturada } do 422 (handoff 0107 §5.3).
   // eslint-disable-next-line react/forbid-prop-types
   itemErrors: PropTypes.object,
   onCancel: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
};
